import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { convertDateToWords } from "../utils/dateToWords";

/**
 * receiptData.data is expected to be a FLAT array of term-records
 * across possibly MANY students, e.g.:
 * [
 *   { id: "A1", examType: "First",  name: "Ravi", marks: [...], ... },
 *   { id: "A1", examType: "Second", name: "Ravi", marks: [...], ... },
 *   { id: "A1", examType: "Final",  name: "Ravi", marks: [...], ... },
 *   { id: "A2", examType: "First",  name: "Sita", marks: [...], ... },
 *   ...
 * ]
 *
 * We group these by student identity, then render one
 * profile-page + marksheet-page pair per student.
 *
 * Two customization controls sit above the receipts (hidden on print):
 *  - a theme/accent color picker (borders + header shading)
 *  - a term filter (show one term at a time, or all three)
 */

const TERMS = [
  { key: "First", label: "Term-I" },
  { key: "Second", label: "Term-II" },
  { key: "Final", label: "Final Exam" },
];

const toCamelCase = (str) => {
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

function ProfileLine({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "220px auto",
        mb: 0.5,
      }}
    >
      <Typography sx={{ fontWeight: "bold" }}>{label} :</Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

// Renders the full 2-page (profile + marksheet) block for ONE student.
function StudentMarksheet({ records, school, isLast, themeColor, selectedTerm }) {
  // extract unique subjects across this student's terms
  const subjects = useMemo(() => {
    const allMarks = records.flatMap((r) => r.marks || []);
    const unique = [
      ...new Map(allMarks.map((m) => [m.subjectName.trim(), m])).values(),
    ];
    return unique.sort((a, b) =>
      a.subjectName.toLowerCase().localeCompare(b.subjectName.toLowerCase())
    );
  }, [records]);

  const findTerm = (examType) =>
    records.find((r) =>
      r.examType?.trim().toLowerCase().includes(examType?.trim().toLowerCase())
    );

  const findMark = (examType, subject) => {
    const term = findTerm(examType);
    return term?.marks.find(
      (s) => s.subjectName.trim().toLowerCase() === subject.trim().toLowerCase()
    );
  };

  const getTotals = (examType) => {
    const term = findTerm(examType);
    if (!term) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade };
  };

  // Only the term(s) currently selected in the filter get rendered.
  const visibleTerms =
    selectedTerm === "all" ? TERMS : TERMS.filter((t) => t.key === selectedTerm);

  const totalsByTerm = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotals(t.key);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  // Use the "Final" term record for result/remarks/percentage.
  // Falls back to the last record if no "Final" term exists,
  // instead of assuming a fixed array index (fixes the old students[2] bug).
  const finalRecord = findTerm("Final") || records[records.length - 1];

  // Profile info comes from the "First" term record (falls back to the first record).
  const profileRecord = findTerm("First") || records[0];

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);
  const cellSx = { border: `1px solid ${borderColor}`, padding: "8px" };
  const headCellSx = { ...cellSx, background: headerBg };

  return (
    <>
      {/* PAGE 1: Student Profile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          justifyContent: "space-around",
          alignItems: "center",
          alignContent: "center",
          marginTop: 2,
          p: 1,
          background: "#fff",
          fontFamily: "Arial",
          maxWidth: "800px",
          margin: "auto",
          px: 5,
          pb: 5,
          border: `2px solid ${borderColor}`,
          borderRadius: "12px",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignContent: "center",
              justifyContent: "space-between",
              flexDirection: "column",
              alignItems: "center",
              mb: 1,
              mt: 5,
            }}
          >
            <img
              src={school.logoUrl}
              alt="School Logo"
              style={{ width: 100, height: 90, marginTop: 20 }}
            />

            <Typography variant="h4" align="center" fontWeight="bold" sx={{ color: borderColor }}>
              {toCamelCase(school.schoolName)}
            </Typography>
            <Typography variant="subtitle1" align="center">
              {toCamelCase(
                [
                  school?.addressDTO.addressLine1,
                  school?.addressDTO.city,
                  school?.addressDTO.pincode,
                  school?.addressDTO.state,
                ].join(", ")
              )}
            </Typography>
            <Typography variant="subtitle1" align="center" fontWeight="bold">
              PROGRESS REPORT SESSION - {profileRecord?.academicYear}
            </Typography>
            <img
              src={profileRecord?.photo}
              alt={profileRecord?.photo}
              style={{ width: 100, height: 100, marginTop: 10 }}
            />
          </Box>
          <Typography variant="subtitle1" align="center" fontWeight="bold">
            {"STUDENT PROFILE"}
          </Typography>
          <Divider sx={{ mb: 2, borderColor }} />

          {/* Student Info */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              flexDirection: "column",
            }}
          >
            <Box sx={{ lineHeight: 2 }}>
              <ProfileLine
                label="Name of the student"
                value={toCamelCase(`${profileRecord?.name} ${profileRecord?.last}`)}
              />
              <ProfileLine
                label="Father's name"
                value={toCamelCase(`${profileRecord?.father}  ${profileRecord?.last}`)}
              />
              <ProfileLine
                label="Mother's name"
                value={toCamelCase(`${profileRecord?.mother}  ${profileRecord?.last}`)}
              />
              <ProfileLine
                label="Studied"
                value={`${profileRecord?.classRoom?.standard} | Roll No : ${
                  profileRecord?.classRoom?.rollNumber || ""
                }`}
              />
              <ProfileLine
                label="Admission No."
                value={`${profileRecord?.id || ""}  |  Caste: ${
                  profileRecord?.caste || ""
                }`}
              />
              <ProfileLine
                label="Date of Birth"
                value={
                  profileRecord?.birthday
                    ? new Date(profileRecord.birthday).toLocaleDateString("en-GB")
                    : ""
                }
              />

              <Typography>
                <strong>{"(In words) : "}</strong>{" "}
                {convertDateToWords(profileRecord?.birthday)}
              </Typography>
            </Box>

            <Box display={"flex"} justifyContent={"space-between"}>
              <Typography variant="subtitle1" fontWeight={700}>
                {"Height/Weight"}
              </Typography>
              <TextField
                variant="standard"
                placeholder="..............."
                slotProps={{
                  input: {
                    disableUnderline: true,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      {/* PAGE 2: Marksheet */}
      <Box
        sx={{
          p: 1,
          background: "#fff",
          fontFamily: "Arial",
          maxWidth: "800px",
          margin: "auto",
          px: 5,
          pb: 5,
          border: `2px solid ${borderColor}`,
          borderRadius: "12px",
          backgroundColor: "#fff",
        }}
        style={{
          backgroundImage: `url(${school.logoUrl})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundColor: "rgb(255 255 255 / 87%)",
          backgroundBlendMode: "lighten",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            mb: 1,
            mt: 0,
          }}
        >
          <Typography variant="h6" align="center" fontWeight="bold" sx={{ color: borderColor }}>
            {toCamelCase(school.schoolName)}
          </Typography>
          <Typography variant="subtitle1" align="center">
            {toCamelCase(
              [school.addressLine2, school.city, school.postalCode, school.state].join(
                ", "
              )
            )}
          </Typography>
          <Typography align="center" sx={{ fontWeight: "bold", my: 1 }}>
            PROGRESS REPORT SESSION - {profileRecord?.academicYear}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2, mt: 1, borderColor }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography>
            <strong>Student's Name: </strong>{" "}
            {toCamelCase(
              `${[profileRecord?.name, profileRecord?.father, profileRecord?.last]
                .filter(Boolean)
                .join(" ")}`
            )}
          </Typography>
          <Typography>
            <strong>Standard: </strong>
            {profileRecord?.classRoom?.standard} | {profileRecord?.classRoom?.section}
          </Typography>
          <Typography>
            <strong>Roll No.: </strong> {profileRecord?.classRoom?.rollNumber}
          </Typography>
        </Box>
        <Divider sx={{ m: 1, borderColor }} />

        {/* Marks Table */}
        <Box sx={{ py: 2, mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx }} rowSpan={2} align="center">
                  <strong>Subject</strong>
                </TableCell>

                {visibleTerms.map((term) => (
                  <TableCell key={term.key} sx={{ ...headCellSx }} colSpan={4} align="center">
                    <strong>{term.label}</strong>
                  </TableCell>
                ))}
              </TableRow>

              <TableRow>
                {visibleTerms.flatMap((term) => [
                  <TableCell key={`max-${term.key}`} sx={headCellSx} align="center">
                    Max
                  </TableCell>,
                  <TableCell key={`min-${term.key}`} sx={headCellSx} align="center">
                    Min
                  </TableCell>,
                  <TableCell key={`obt-${term.key}`} sx={headCellSx} align="center">
                    Obt
                  </TableCell>,
                  <TableCell key={`grade-${term.key}`} sx={headCellSx} align="center">
                    Grade
                  </TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => (
                <TableRow key={i}>
                  <TableCell sx={cellSx}>{toCamelCase(sub.subjectName)}</TableCell>

                  {visibleTerms.flatMap((term) => {
                    const mark = findMark(term.key, sub.subjectName);
                    return [
                      <TableCell key={`${term.key}-max-${i}`} sx={cellSx} align="center">
                        {mark?.maxMark || 100}
                      </TableCell>,
                      <TableCell key={`${term.key}-min-${i}`} sx={cellSx} align="center">
                        {(mark?.maxMark * 34) / 100 || 35}
                      </TableCell>,
                      <TableCell key={`${term.key}-obt-${i}`} sx={cellSx} align="center">
                        {mark?.marksObtained || "-"}
                      </TableCell>,
                      <TableCell key={`${term.key}-grade-${i}`} sx={cellSx} align="center">
                        {mark?.grade || "-"}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              ))}

              {/* Totals Row */}
              <TableRow sx={{ background: totalsBg }}>
                <TableCell sx={cellSx}>
                  <strong>Total Marks</strong>
                </TableCell>

                {visibleTerms.flatMap((term) => {
                  const t = totalsByTerm[term.key];
                  return [
                    <TableCell key={`${term.key}-tmax`} sx={cellSx} align="center">
                      {t.max}
                    </TableCell>,
                    <TableCell key={`${term.key}-tmin`} sx={cellSx} />,
                    <TableCell key={`${term.key}-tobt`} sx={cellSx} align="center">
                      {t.obt}
                    </TableCell>,
                    <TableCell key={`${term.key}-tgrade`} sx={cellSx} align="center">
                      {t.grade}
                    </TableCell>,
                  ];
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="body2">
            <strong>Result:</strong> {finalRecord?.result}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <b>Remarks: </b>
            {finalRecord?.remarks}
          </Typography>
          <Typography variant="body2">
            <strong>Percentage:</strong> {finalRecord?.percentage}%
          </Typography>
        </Box>

        {/* Footer */}
        <Box my={8} display="flex" justifyContent="space-between">
          <Typography variant="body2">Signature: ___________________</Typography>
          <Typography variant="body2">Class Teacher Sign.: ___________________</Typography>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {" "}
              Principal/HeadMaster{" "}
            </Typography>
            <Typography variant="body2">Seal/Sign.: ___________________</Typography>
          </Box>
        </Box>
      </Box>

      {/* page break between students (not after the very last one) */}
      {!isLast && <div className="pagebreak"></div>}
    </>
  );
}

const SinglePageMarksheetReceiptBulk = ({ receiptData, school, setTemID }) => {
  const allRecords = receiptData.data || [];
  const receiptId = `marksheet`;

  // --- Customization controls (theme color + term filter) ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all"); // "all" | "First" | "Second" | "Final"

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Group flat term-records into one array per student.
  // Grouping key preference: admission id -> rollNumber -> full name (fallback).
  const studentGroups = useMemo(() => {
    const groups = new Map();

    allRecords.forEach((record) => {
      const key =
        record.id ??
        record.classRoom?.rollNumber ??
        `${record.name || ""}-${record.last || ""}-${record.father || ""}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(record);
    });

    return Array.from(groups.values());
  }, [allRecords]);

  return (
    <Box id={receiptId}>
      {/* Controls: hidden when printing */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "800px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Theme Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 40,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="term-filter-label">Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {TERMS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {studentGroups.map((records, idx) => (
        <StudentMarksheet
          key={records[0]?.id ?? idx}
          records={records}
          school={school}
          isLast={idx === studentGroups.length - 1}
          themeColor={themeColor}
          selectedTerm={selectedTerm}
        />
      ))}
    </Box>
  );
};

export default SinglePageMarksheetReceiptBulk;
